import {
  buildCyberTracerExportFilename,
  buildDebugBundlePayload,
  exportToPDF,
} from '../../services/intelligence_exporter';
import { buildStoreOnlyZip } from './storeOnlyZip';

/**
 * Format complete scan logs as plain text (full trail for QA).
 *
 * @param {Array<object|string>} logs
 * @returns {string}
 */
export function formatCompleteLogsText(logs = []) {
  const lines = [];
  const list = Array.isArray(logs) ? logs : [];
  for (const entry of list) {
    if (typeof entry === 'string') {
      lines.push(entry);
      continue;
    }
    if (!entry || typeof entry !== 'object') continue;
    const ts = String(entry.timestamp || entry.ts || '').trim();
    const msg = String(entry.message || entry.text || entry.msg || '').trim();
    if (ts && msg) lines.push(`[${ts}] ${msg}`);
    else if (msg) lines.push(msg);
    else lines.push(JSON.stringify(entry));
  }
  if (!lines.length) {
    return 'No log entries captured for this search.\n';
  }
  return `${lines.join('\n')}\n`;
}

/**
 * Split admin SSE event list into intel rows, logs, and completion leads.
 *
 * @param {unknown[]} events
 * @returns {{
 *   results: object[],
 *   logs: object[],
 *   extractedLeads: object,
 *   completion: object|null,
 * }}
 */
export function partitionSseEvents(events = []) {
  const results = [];
  const logs = [];
  let extractedLeads = {
    emails: [],
    phones: [],
    cuis: [],
    associated_urls: [],
    associated_urls_partial: [],
    pivots: [],
  };
  let completion = null;

  for (const ev of Array.isArray(events) ? events : []) {
    if (!ev || typeof ev !== 'object') continue;
    if (ev.session_id && Object.keys(ev).length <= 2) continue;

    const isTerminalDone = ev.done === true;
    const isOfficialEnd =
      isTerminalDone ||
      (ev.status === 'completed' &&
        !ev.ui_bucket &&
        !ev.source &&
        ev.type !== 'log') ||
      ev.status === 'cancelled';

    if (isOfficialEnd) {
      completion = ev;
      if (ev.extracted_leads && typeof ev.extracted_leads === 'object') {
        extractedLeads = {
          ...extractedLeads,
          ...ev.extracted_leads,
        };
      }
      continue;
    }

    if (ev.type === 'log') {
      logs.push(ev);
      continue;
    }

    if (ev.type === 'cache_meta') continue;
    results.push(ev);
  }

  return { results, logs, extractedLeads, completion };
}

/**
 * Infer module + target from an admin last_searches row.
 *
 * @param {object} search
 * @returns {{ moduleName: string, target: string }}
 */
export function resolveAdminSearchExportMeta(search) {
  const path = String(search?.path || search?.full_url || '').toLowerCase();
  const q = search?.query && typeof search.query === 'object' ? search.query : {};
  if (path.includes('phone')) {
    return {
      moduleName: 'phone',
      target: String(q.phone || q.name || 'phone-target').trim() || 'phone-target',
    };
  }
  if (path.includes('email')) {
    return {
      moduleName: 'email',
      target: String(q.email || q.name || 'email-target').trim() || 'email-target',
    };
  }
  if (path.includes('company') || path.includes('corporate')) {
    return {
      moduleName: 'company',
      target:
        String(q.domain || q.name || q.primary_domain || 'company-target').trim() ||
        'company-target',
    };
  }
  return {
    moduleName: 'person',
    target: String(q.name || q.query || 'person-target').trim() || 'person-target',
  };
}

/**
 * Build ZIP: debug JSON + complete logs + PDF report.
 *
 * @param {{
 *   target?: string,
 *   moduleName?: string,
 *   results?: object[],
 *   logs?: object[],
 *   extractedLeads?: object,
 * }} opts
 * @returns {Promise<{ blob: Blob, filename: string }>}
 */
export async function buildSearchArtifactBundle(opts = {}) {
  const target = String(opts.target || 'target').trim() || 'target';
  const moduleName = String(opts.moduleName || 'intelligence').trim() || 'intelligence';
  const results = Array.isArray(opts.results) ? opts.results : [];
  const logs = Array.isArray(opts.logs) ? opts.logs : [];
  const extractedLeads = opts.extractedLeads || {};

  const debugPayload = buildDebugBundlePayload({
    target,
    results,
    logs,
    extractedLeads,
    moduleName,
  });
  const debugJson = `${JSON.stringify(debugPayload, null, 2)}\n`;
  const logsText = formatCompleteLogsText(logs);

  let pdfBlob;
  try {
    pdfBlob = await exportToPDF({
      target,
      results,
      logs,
      extractedLeads,
      moduleName,
      returnBlob: true,
    });
  } catch (err) {
    pdfBlob = new Blob(
      [
        `PDF generation failed: ${String(err?.message || err)}\n`,
        'Use debug JSON + complete logs for offline review.\n',
      ],
      { type: 'text/plain;charset=utf-8' },
    );
  }

  const stem = buildCyberTracerExportFilename({
    moduleName,
    target,
    extension: 'zip',
  }).replace(/\.zip$/i, '');

  const zipBlob = await buildStoreOnlyZip([
    { name: `${stem}-debug.json`, data: debugJson },
    { name: `${stem}-complete-logs.txt`, data: logsText },
    {
      name:
        pdfBlob?.type === 'application/pdf' || String(pdfBlob?.type || '').includes('pdf')
          ? `${stem}.pdf`
          : `${stem}-pdf-error.txt`,
      data: pdfBlob,
    },
  ]);

  return {
    blob: zipBlob,
    filename: `${stem}-bundle.zip`,
  };
}

/**
 * Trigger browser download of the artifact ZIP.
 *
 * @param {Parameters<typeof buildSearchArtifactBundle>[0]} opts
 */
export async function downloadSearchArtifactBundle(opts = {}) {
  const { blob, filename } = await buildSearchArtifactBundle(opts);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { filename, blob };
}

/**
 * Download bundle reconstructed from an admin last_searches SSE record.
 *
 * @param {object} search
 * @returns {Promise<{ filename: string, blob: Blob }|null>}
 */
export async function downloadBundleFromAdminSearch(search) {
  const events = Array.isArray(search?.result_json?.events) ? search.result_json.events : [];
  if (!events.length) return null;
  const { results, logs, extractedLeads } = partitionSseEvents(events);
  const meta = resolveAdminSearchExportMeta(search);
  return downloadSearchArtifactBundle({
    ...meta,
    results,
    logs,
    extractedLeads,
  });
}
